import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useDisclosure,
  HStack,
  Spinner,
  Text,
  Alert,
  AlertIcon,
  Badge,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiEyeOff, FiEye } from 'react-icons/fi';

const API_BASE = '/api';

function Settings() {
  const toast = useToast();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSetting, setEditingSetting] = useState(null);
  const [settingForm, setSettingForm] = useState({ key: '', value: '', tenant_id: '' });
  const [showValues, setShowValues] = useState({});
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [settingToDelete, setSettingToDelete] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchSettings = () => {
    fetch(`${API_BASE}/settings/`, { headers: getAuthHeaders() })
      .then(res => res.json())
      .then(data => {
        setSettings(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const openAddSetting = () => {
    setEditingSetting(null);
    setSettingForm({ key: '', value: '', tenant_id: '' });
    onModalOpen();
  };

  const openEditSetting = (setting) => {
    setEditingSetting(setting);
    setSettingForm({ key: setting.key, value: setting.value, tenant_id: setting.tenant_id || '' });
    onModalOpen();
  };

  const handleSaveSetting = async () => {
    const method = editingSetting ? 'PUT' : 'POST';
    const url = editingSetting ? `${API_BASE}/settings/${editingSetting.id}/` : `${API_BASE}/settings/`;
    
    const payload = editingSetting
      ? { value: settingForm.value, tenant_id: settingForm.tenant_id }
      : settingForm;
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        fetchSettings();
        toast({ title: editingSetting ? 'Setting updated' : 'Setting created', status: 'success', duration: 3000 });
        onModalClose();
      } else {
        toast({ title: data.error || 'Error saving setting', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving setting', status: 'error', duration: 3000 });
    }
  };

  const confirmDelete = (setting) => {
    setSettingToDelete(setting);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!settingToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/settings/${settingToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchSettings();
        toast({ title: 'Setting deleted', status: 'success', duration: 3000 });
      } else {
        toast({ title: 'Error deleting setting', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting setting', status: 'error', duration: 3000 });
    }
    onDeleteClose();
    setSettingToDelete(null);
  };

  const toggleShowValue = (id) => {
    setShowValues({ ...showValues, [id]: !showValues[id] });
  };

  const isSensitive = (key) => {
    const sensitiveKeys = ['secret', 'password', 'key', 'token', 'private'];
    return sensitiveKeys.some(k => key.toLowerCase().includes(k));
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="teal.500" /></Box>;
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Settings</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="teal" onClick={openAddSetting}>
          Add Setting
        </Button>
      </HStack>

      <Alert status="info" mb={6} borderRadius="md">
        <AlertIcon />
        Settings are key-value pairs used for configuration. Sensitive values (containing "secret", "key", "password") are hidden by default. Leave Tenant ID empty for default settings that apply across all tenants.
      </Alert>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Key</Th>
              <Th>Tenant</Th>
              <Th>Value</Th>
              <Th>Updated</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {settings.map(setting => (
              <Tr key={setting.id}>
                <Td fontWeight="medium" fontFamily="mono">{setting.key}</Td>
                <Td>
                  {setting.tenant_id
                    ? <Badge colorScheme="purple">{setting.tenant_id}</Badge>
                    : <Badge colorScheme="gray">default</Badge>}
                </Td>
                <Td>
                  <HStack>
                    <Text fontSize="sm" fontFamily="mono" maxW="300px" isTruncated>
                      {isSensitive(setting.key) && !showValues[setting.id] 
                        ? '••••••••••••' 
                        : setting.value}
                    </Text>
                    {isSensitive(setting.key) && (
                      <IconButton
                        icon={showValues[setting.id] ? <FiEyeOff /> : <FiEye />}
                        size="xs"
                        variant="ghost"
                        onClick={() => toggleShowValue(setting.id)}
                        aria-label="Toggle visibility"
                      />
                    )}
                  </HStack>
                </Td>
                <Td>{new Date(setting.updated_at).toLocaleDateString()}</Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FiEdit2 />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditSetting(setting)}
                      aria-label="Edit"
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => confirmDelete(setting)}
                      aria-label="Delete"
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {settings.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>No settings found</Text>
        )}
      </Box>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingSetting ? 'Edit Setting' : 'Add Setting'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={4}>
              <FormLabel>Key</FormLabel>
              <Input
                value={settingForm.key}
                onChange={(e) => setSettingForm({ ...settingForm, key: e.target.value })}
                placeholder="setting_key"
                isDisabled={!!editingSetting}
                fontFamily="mono"
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Tenant ID</FormLabel>
              <Input
                value={settingForm.tenant_id}
                onChange={(e) => setSettingForm({ ...settingForm, tenant_id: e.target.value })}
                placeholder="Leave empty for default"
                fontFamily="mono"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Value</FormLabel>
              <Textarea
                value={settingForm.value}
                onChange={(e) => setSettingForm({ ...settingForm, value: e.target.value })}
                placeholder="setting value"
                fontFamily="mono"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModalClose}>Cancel</Button>
            <Button colorScheme="teal" onClick={handleSaveSetting}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Setting</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete the setting <strong>{settingToDelete?.key}</strong>? 
            This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Settings;
